import React, { ReactNode } from 'react'

import LazyLoad from 'react-lazyload'
import EntryMarker from 'marketing-site/src/components/common/EntryMarker'
import {
  IVideo as IContentfulVideo,
  IMediaByBreakpoint as IContentfulMediaByBreakpoint,
  IImage as IContentfulImage,
} from 'marketing-site/@types/generated/contentful'
import { transformImage } from 'marketing-site/src/transformers/elements/ContentfulImage'
import { transformVideoWithImageProps } from 'marketing-site/src/transformers/elements/ContentfulMedia'
import { Image } from 'marketing-site/src/library/elements/Image'
import { VideoAutoplay } from 'marketing-site/src/library/elements/VideoAutoplay'
import {
  IProps as IMediaByBreakpoint,
  MediaByBreakpoint,
} from 'marketing-site/src/library/elements/MediaByBreakpoint'

export const ContentfulMediaByBreakpoint = (
  mediaByBreakpoint: IContentfulMediaByBreakpoint,
): React.ReactElement => {
  return (
    <EntryMarker entry={mediaByBreakpoint}>
      {() => <MediaByBreakpoint {...transformMediaByBreakpoints(mediaByBreakpoint)} />}
    </EntryMarker>
  )
}

function transformVideoOrImage(
  media: IContentfulVideo | IContentfulImage,
  noLazyLoad?: boolean,
): ReactNode {
  if (media.sys.contentType.sys.id === 'image') {
    const imageProps = transformImage(media as IContentfulImage)
    const imageComponent = <Image {...imageProps} />

    return noLazyLoad ? imageComponent : withLazyLoad(imageComponent, imageProps.height)
  } else if (media.sys.contentType.sys.id === 'video') {
    const videoProps = transformVideoWithImageProps(media as IContentfulVideo)

    return <VideoAutoplay {...videoProps} noLazyLoad={noLazyLoad} />
  }
}

export function transformMediaByBreakpoints(
  mediaByBreakpoint: IContentfulMediaByBreakpoint,
  noLazyLoad?: boolean,
): IMediaByBreakpoint {
  return {
    mobileMedia:
      mediaByBreakpoint.fields.mobileMedia &&
      transformVideoOrImage(mediaByBreakpoint.fields.mobileMedia, noLazyLoad),
    tabletMedia:
      mediaByBreakpoint.fields.tabletMedia &&
      transformVideoOrImage(mediaByBreakpoint.fields.tabletMedia, noLazyLoad),
    desktopMedia:
      mediaByBreakpoint.fields.desktopMedia &&
      transformVideoOrImage(mediaByBreakpoint.fields.desktopMedia, noLazyLoad),
    desktopLargeMedia:
      mediaByBreakpoint.fields.desktopLargeMedia &&
      transformVideoOrImage(mediaByBreakpoint.fields.desktopLargeMedia, noLazyLoad),
  }
}

const withLazyLoad = (component: React.ReactNode, height?: number | string) => {
  return (
    <LazyLoad offset={0} height={height} once>
      {component}
    </LazyLoad>
  )
}
